import { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';

import FormModal from '#client/components/FormModal';
import { ADDRESS } from '#client/constants/application';
import { CARD_TYPE } from '#client/constants/cards';
import { COMMON_ERROR_MESSAGE_DESC, COMMON_ERROR_MESSAGE_TITLE } from '#client/constants/messages';
import useCardAddress from '#client/hooks/useCardAddress';
import { FormStepProvider } from '#client/hooks/useFormStep';
import useOrderCard from '#client/hooks/useOrderCard';
import useToastContext from '#client/hooks/useToastContext';

import ChooseCardFrom from './components/ChooseCard';
import ReasonOfRequestingFrom from './components/ReasonOfRequesting';
import SendingAddress from './components/SendingAddress';
import Summary from './components/Summary';

import {
  CHOOSE_CARD,
  REASON,
  REASON_OF_REQUESTING,
  REASON_PAYLOAD,
  SENDING_ADDRESS,
  SUMMARY
} from './constants/card';
import useReplaceCard from './hooks/useReplaceCard';

const emptyAddress = {
  street: '',
  street2: '',
  city: '',
  state: '',
  postalCode: '',
  country: ''
};

export default function CreateNewCardModal({ handleClose }) {
  const [data, setData] = useState({
    cardId: '',
    cardType: '',
    cardStatus: '',
    reason: REASON.LOST,
    selectedAddress: ADDRESS.BUSINESS,
    businessAddress: {},
    personalAddress: {}
  });
  const [currentFormStep, setCurrentFormStep] = useState(CHOOSE_CARD);
  const { loading: submitLoading, handleReplaceCard } = useReplaceCard();
  const { createErrorToast } = useToastContext();
  const { cardType, businessAddress, personalAddress, selectedAddress, cardId, reason } = data;
  const { handleSubmitAddress, loading: createCardLoading } = useOrderCard();
  const {
    businessAddress: applicationBusinessAddress,
    personalAddress: applicationPersonalAddress,
    loading: applicationLoading,
    error: applicationError
  } = useCardAddress();

  useEffect(() => {
    if (!applicationLoading) {
      setData({
        ...data,
        businessAddress: applicationBusinessAddress ?? emptyAddress,
        personalAddress: applicationPersonalAddress ?? emptyAddress
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationLoading]);

  useEffect(() => {
    if (applicationError) {
      createErrorToast({
        title: COMMON_ERROR_MESSAGE_TITLE,
        description: COMMON_ERROR_MESSAGE_DESC
      });
    }
  }, [applicationError, createErrorToast]);

  const reasonPayload = useMemo(() => {
    if (cardType === CARD_TYPE.BUSINESS_VIRTUAL_DEBIT_CARD) {
      return REASON_PAYLOAD.STOLEN;
    }

    switch (reason) {
      case REASON.DAMAGED:
        return REASON_PAYLOAD.REPLACE;
      case REASON.LOST:
        return REASON_PAYLOAD.LOST;
      case REASON.STOLEN:
        return REASON_PAYLOAD.STOLEN;
      default:
        return REASON_PAYLOAD.STOLEN;
    }
  }, [cardType, reason]);

  const handleSubmitData = async () => {
    await handleReplaceCard({
      businessAddress,
      cardId,
      cardType,
      handleClose,
      personalAddress,
      reason: reasonPayload,
      selectedAddress
    });
  };

  const handleCreateNewCard = async (address) => {
    if (address === 'business') {
      await handleSubmitAddress(businessAddress);
    } else {
      await handleSubmitAddress(personalAddress);
    }
    handleClose();
  };

  return (
    <FormModal handleClose={handleClose}>
      <FormStepProvider
        value={{
          currentFormStep,
          setCurrentFormStep,
          data,
          setData
        }}
      >
        {currentFormStep === CHOOSE_CARD && (
          <ChooseCardFrom
            applicationLoading={applicationLoading}
            onClose={handleClose}
          />
        )}
        {currentFormStep === REASON_OF_REQUESTING && <ReasonOfRequestingFrom />}
        {currentFormStep === SENDING_ADDRESS && (
          <SendingAddress
            createCardLoading={createCardLoading}
            handleCreateNewCard={handleCreateNewCard}
            loading={applicationLoading}
          />
        )}
        {currentFormStep === SUMMARY && (
          <Summary
            isSubmitLoading={submitLoading}
            onSubmitData={handleSubmitData}
          />
        )}
      </FormStepProvider>
    </FormModal>
  );
}

CreateNewCardModal.propTypes = {
  handleClose: PropTypes.func.isRequired
};
